/**
 * The sword part shapes (parts.ts registries): blade profiles, guards, pommels, grip wraps, tassels, scabbards.
 * Sizes are set for the figure at game size (docs/review/sword/2026-10-11-r1: the figure's dark outline swallows a
 * 3-px blade, so every blade is at least 5 fine px at the root; guards stand 2+ px clear of the blade either side).
 */
import { BLADES, GRIPS, GUARDS, POMMELS, SCABBARDS, TASSELS, type TasselStyle } from './parts';

// ── blade profiles ──
/** The point: the half-width runs down to nothing over the last `pt` of the blade, never under one pixel before. */
const pointed = (h: number, t: number, pt: number) => (t > 1 - pt ? h * Math.max(0, (1 - t) / pt) : h);
const blade = (id: string, length: number, pt: number, body: (t: number) => number) =>
  BLADES.set(id, { length, half: (t) => Math.max(pointed(body(t), t, pt), t < 1 ? 0.5 : 0) });
// C, the hero's: broad at the root, a strong gentle taper, a short angled point.
blade('hero', 26, 0.12, (t) => 2.0 - 0.5 * t);
// Slim (a scholar's): tapering to 3 px by the middle.
blade('slim', 25, 0.14, (t) => 2.0 - 1.0 * t);
// Long: a longer blade that keeps its width.
blade('long', 29, 0.12, (t) => 2.0 - 0.6 * t);
// Broad (a war jian): 7 px at the root.
blade('broad', 25, 0.1, (t) => 3.0 - 0.6 * t);
// Leaf: swelling past the middle before a long point.
blade('leaf', 25, 0.2, (t) => 2.0 + 0.8 * Math.exp(-(((t - 0.6) / 0.2) ** 2)));
// Needle: a long even taper into a thrusting point.
blade('needle', 27, 0.3, (t) => 2.0 - 1.2 * t);
// Short: a short sword.
blade('short', 20, 0.12, (t) => 2.0 - 0.4 * t);
// Stepped: a thick root (ricasso) under the guard, then the hero's taper.
blade('stepped', 26, 0.12, (t) => (t < 0.13 ? 2.8 : 2.0 - 0.5 * t));

// ── guards (劍格) ──
GUARDS.set('wings', {
  // C: a compact block whose wings sweep up toward the blade, tips one pixel.
  end: 1.7,
  maxHalf: 4.2,
  at: (a, ac, u) => {
    const span = 2.1 + Math.max(0, a - 0.5) * 1.8;
    if (a < 0.2 || a > 1.7 || ac > Math.min(span, 4.2) || (ac > 2.2 && a < 0.55 + (ac - 2.2) * 0.5)) return undefined;
    if (ac <= 0.6 && a > 0.55 && a < 1.15) return 'gem';
    return ac > 3.4 ? 0.95 : u * 0.7;
  },
});
GUARDS.set('block', {
  // A plain thick bar with a raised band across its middle.
  end: 1.3,
  maxHalf: 3.1,
  at: (a, ac, u) => {
    if (a < 0.2 || a > 1.3 || ac > 3.0) return undefined;
    if (ac <= 0.6 && a > 0.5 && a < 1.0) return 'gem';
    return a > 0.6 && a < 0.95 ? 0.8 : u * 0.7 - (ac > 2.4 ? 0.3 : 0);
  },
});
GUARDS.set('ruyi', {
  // A waisted bar whose ends curl toward the blade (a ruyi cloud).
  end: 1.4,
  maxHalf: 3.7,
  at: (a, ac, u) => {
    const body = a >= 0.25 && a <= 1.1 && ac <= 2.6 - (ac > 1.4 && a > 0.85 ? 0.6 : 0);
    const curl = ac > 2.0 && ac <= 3.6 && a >= 0.45 && a <= 1.6 && !(ac < 2.9 && a > 0.95);
    if (!body && !curl) return undefined;
    if (body && ac <= 0.6 && a > 0.5 && a < 0.95) return 'gem';
    return curl ? (a > 1.25 ? 0.9 : u > 0 ? 0.4 : -0.9) : u * 0.7;
  },
});
GUARDS.set('mask', {
  // A Han guard with a raised centre (a mask in relief) standing proud toward the blade.
  end: 1.5,
  maxHalf: 3.5,
  at: (a, ac, u) => {
    const bar = a >= 0.25 && a <= 1.15 && ac <= 3.4;
    const boss = ac <= 1.6 && a >= 0 && a <= 1.5;
    if (!bar && !boss) return undefined;
    if (boss) return ac <= 0.6 && a > 0.5 && a < 1.0 ? 'gem' : a > 0.75 ? 0.9 : 0.2;
    return ac > 2.8 ? (u > 0 ? 0.3 : -0.8) : u * 0.7;
  },
});
GUARDS.set('crescent', {
  // Horns curving back toward the grip, like a moon on its back.
  end: 1.3,
  maxHalf: 4,
  at: (a, ac, u) => {
    const body = a >= 0.3 && a <= 1.3 && ac <= 2.3;
    const k = (ac - 2.0) * 0.55;
    const horn = ac > 2.0 && ac <= 3.9 && a >= 0.55 - k - 0.5 && a <= 1.05 - k;
    if (!body && !horn) return undefined;
    if (body && ac <= 0.6 && a > 0.55 && a < 1.05) return 'gem';
    return horn ? (ac > 3.3 ? 0.95 : u * 0.6) : u * 0.7;
  },
});
GUARDS.set('disc', {
  // A round plate seen edge-on.
  end: 1.25,
  maxHalf: 3.4,
  at: (a, ac, u) => {
    if (a < 0.2 || a > 1.25 || ac > 3.3 * Math.sin(Math.PI * (0.16 + 0.68 * ((a - 0.2) / 1.05)))) return undefined;
    if (ac <= 0.6 && a > 0.5 && a < 1.0) return 'gem';
    return a > 0.85 ? 0.85 : u * 0.7;
  },
});
GUARDS.set('lotus', {
  // A lotus cup: a base, and petals cupping the blade's root.
  end: 1.9,
  maxHalf: 3.2,
  at: (a, ac, u) => {
    const base = a >= 0.15 && a <= 0.8 && ac <= 2.4;
    const petal = a > 0.8 && a <= 1.9 && ac <= 3.1 - (a - 0.8) * 1.2 && ac >= 0.9 + (a - 0.8) * 0.9;
    if (!base && !petal) return undefined;
    if (base && ac <= 0.6 && a > 0.3 && a < 0.7) return 'gem';
    return petal ? (Math.abs(ac - (2.0 - (a - 0.8) * 0.15)) < 0.5 ? -0.6 : a > 1.5 ? 0.95 : 0.5) : u * 0.7;
  },
});
GUARDS.set('beast', {
  // A beast's head swallowing the blade: bulky, with two eyes.
  end: 1.9,
  maxHalf: 3.6,
  at: (a, ac, u) => {
    if (a < 0 || a > 1.9 || ac > 3.5 - Math.abs(a - 0.8) * 1.3) return undefined;
    if (ac > 1.1 && ac < 2.1 && a > 0.95 && a < 1.35) return 'eye';
    if (ac <= 0.6 && a > 1.4) return 0.95;
    return a < 0.35 ? -0.5 : u * 0.7 + (a > 1.4 ? 0.2 : 0);
  },
});

// ── pommels (劍首) ──
POMMELS.set('cap', { len: 0.8, maxHalf: 1.7, hang: 0.6, at: (_t, ac, u) => (ac <= 1.6 ? u * 0.7 : undefined) });
POMMELS.set('ring', {
  len: 1.9,
  maxHalf: 2.4,
  hang: 0.95,
  at: (t, ac, u, lenPx) => {
    const r = Math.hypot((t - 0.55) * lenPx, ac);
    return r <= 2.25 && r >= 1.1 ? u * 0.9 : undefined;
  },
});
POMMELS.set('mushroom', {
  len: 1.3,
  maxHalf: 2.2,
  hang: 0.6,
  at: (t, ac, u) => (ac <= 1.1 + 1.1 * Math.min(1, t * 1.6) - (t > 0.82 ? (t - 0.82) * 6 : 0) ? u * (t > 0.6 ? 0.9 : 0.6) : undefined),
});
POMMELS.set('disc', {
  len: 0.9,
  maxHalf: 2.8,
  hang: 0.6,
  at: (t, ac, u) => (ac <= 2.8 - (t > 0.75 || t < 0.12 ? 0.7 : 0) ? (t > 0.4 && t < 0.7 ? 0.85 : u * 0.6) : undefined),
});
POMMELS.set('ruyi', {
  len: 1.3,
  maxHalf: 2.4,
  hang: 0.55,
  at: (t, ac, u) => (ac <= 2.3 - (t > 0.7 && ac < 0.9 ? 1.5 : 0) && !(t > 0.85 && ac > 1.7) ? u * (t > 0.6 ? 0.9 : 0.6) : undefined),
});
POMMELS.set('pearl', {
  // A round knob holding a stone at its end.
  len: 1.6,
  maxHalf: 2.2,
  hang: 0.3,
  at: (t, ac, u, lenPx) => {
    const r = Math.hypot((t - 0.6) * lenPx, ac);
    if (t < 0.2) return ac <= 1.2 ? u * 0.5 : undefined;
    if (r > 2.2) return undefined;
    return r < 0.9 ? 'gem' : u * 0.8;
  },
});
POMMELS.set('bud', {
  // A lotus bud: swelling, then closing to a point.
  len: 1.7,
  maxHalf: 2.2,
  hang: 0.35,
  at: (t, ac, u) => (ac <= 0.6 + 1.6 * Math.sin(Math.PI * Math.min(1, t * 1.1)) ? (Math.abs(ac - 1.0) < 0.4 && t > 0.3 ? -0.5 : u * 0.8) : undefined),
});

// ── grip wraps ──
GRIPS.set('cord', { at: (a, c, _u, base, light, res) => ((Math.floor(a * res * 0.75) + (c > 0 ? 1 : 0)) % 2 ? base : light) });
GRIPS.set('ray', { at: (a, c, _u, base, light, res) => ((Math.floor(a * res) * 7 + Math.floor(c + 2) * 3) % 5 === 0 ? light : base) });
GRIPS.set('wood', { at: (_a, _c, u, base, light) => (u > 0.3 ? light : base) });
GRIPS.set('silk', { at: (a, c, _u, base, light, res) => (Math.floor(a * res * 0.9 + c * 0.6) % 2 ? base : light) });
GRIPS.set('leather', { at: (a, _c, u, base, light, res) => (Math.floor(a * res) % 3 === 0 ? base : u > 0 ? light : base) });

// ── tassels (劍穗) ──
/** Knot (2×2), an optional bead (2×2), then strands that widen past their middle; returns where it ended. */
function strands(
  put: (x: number, y: number, col: string) => void,
  top: { x: number; y: number },
  silk: readonly [string, string, string, string],
  bead: string | undefined,
  sway: (i: number, n: number) => number,
  len: number,
  width: number,
  flare = 1,
): void {
  let y = top.y;
  put(top.x, y, silk[3]);
  put(top.x + 1, y, silk[2]);
  y++;
  put(top.x, y, silk[2]);
  put(top.x + 1, y, silk[3]);
  y++;
  if (bead) {
    put(top.x, y, bead);
    put(top.x + 1, y, bead);
    put(top.x, y + 1, bead);
    put(top.x + 1, y + 1, silk[3]);
    y += 2;
  }
  for (let i = 0; i < len; i++) {
    const w = width + (i > len * 0.5 ? flare : 0);
    const dx = sway(i, len);
    for (let s = 0; s < w; s++) {
      const x = top.x - Math.floor((w - 2) / 2) + s + dx;
      const col = i >= len - 1 ? silk[3] : s === 0 ? silk[1] : s === w - 1 ? silk[3] : silk[2];
      put(x, y + i, col);
    }
  }
}
const tassel = (id: string, t: TasselStyle) => TASSELS.set(id, t);
tassel('cord', {
  draw: (put, top, silk, _bead, sway) => {
    put(top.x, top.y, silk[2]);
    put(top.x + 1, top.y, silk[3]);
    for (let i = 1; i <= 4; i++) put(top.x + sway(i, 4), top.y + i, i === 4 ? silk[3] : silk[1]);
  },
});
tassel('short', { draw: (put, top, silk, bead, sway) => strands(put, top, silk, bead, sway, 6, 2) });
tassel('long', { draw: (put, top, silk, bead, sway) => strands(put, top, silk, bead, sway, 10, 2) });
tassel('plume', { draw: (put, top, silk, bead, sway) => strands(put, top, silk, bead, sway, 13, 3) });
tassel('twin', {
  draw: (put, top, silk, bead, sway) => {
    strands(put, { x: top.x - 1, y: top.y }, silk, bead, sway, 8, 1, 1);
    strands(put, { x: top.x + 2, y: top.y + 1 }, silk, undefined, (i, n) => sway(i, n) + (i > n * 0.6 ? 1 : 0), 6, 1, 1);
  },
});
tassel('pendant', {
  // A cord, a jade disc, a short fringe under it.
  draw: (put, top, silk, bead, sway) => {
    const stone = bead ?? '#6cae98';
    for (let i = 0; i < 3; i++) put(top.x + sway(i, 8), top.y + i, i ? silk[1] : silk[3]);
    const p = { x: top.x - 1 + sway(3, 8), y: top.y + 3 };
    for (let y = 0; y < 3; y++) for (let x = 0; x < 3; x++) put(p.x + x, p.y + y, x === 1 && y === 1 ? '#f9ebe7' : stone);
    for (let i = 0; i < 3; i++) {
      put(p.x + sway(6 + i, 8), p.y + 3 + i, silk[2]);
      put(p.x + 2 + sway(6 + i, 8), p.y + 3 + i, i === 2 ? silk[3] : silk[1]);
    }
  },
});

// ── scabbards (劍鞘) ──
SCABBARDS.set('plain', { wider: 0.9, locket: 1.2, bands: [0.3], chape: 'round', chapeLen: 2.4 });
SCABBARDS.set('banded', { wider: 0.9, locket: 1.3, bands: [0.22, 0.42], chape: 'round', chapeLen: 2.6 });
SCABBARDS.set('cloud', { wider: 0.9, locket: 1.4, bands: [0.2, 0.38], chape: 'cloud', chapeLen: 2.8 });
SCABBARDS.set('han', { wider: 0.8, locket: 1.0, bands: [], slide: [0.28, 0.42], chape: 'trap', chapeLen: 2.4 });
SCABBARDS.set('ornate', { wider: 1.0, locket: 2.0, bands: [0.2, 0.36, 0.62], chape: 'flare', chapeLen: 3.2 });
SCABBARDS.set('wrapped', { wider: 0.9, locket: 1.2, bands: [], wrap: 4, chape: 'point', chapeLen: 2.6 });
SCABBARDS.set('travel', { wider: 1.1, locket: 1.3, bands: [0.25, 0.5, 0.75], chape: 'round', chapeLen: 2.4 });
